import React, { Suspense, lazy } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import Loader from "../components/generic/Loader";

const Dashboard = lazy(() => import("../pages/dashboard"));
const Labelling = lazy(() => import("../pages/labelling/index"));
const Guides = lazy(() => import("../pages/labelling/guides"));
const EditStations = lazy(() => import("../pages/labelling/edit-stations"));
const ServerSettings = lazy(() => import("../pages/labelling/server-settings"));
const BugReport = lazy(() => import("../pages/labelling/bug-report"));
const LabelEditor = lazy(() => import("../pages/labelling/editor"));
const LabelGenerator = lazy(() => import("../pages/labelling/generator"));
const LabelDesigner = lazy(() => import("../pages/labelling/designs/index"));
const SingleStation = lazy(() => import("../pages/labelling/[slug]"));
const LaserTemplate = lazy(() => import("../pages/labelling/[slug]/laser-template/index"));
const LabelMachine = lazy(() => import("../pages/labelling/[slug]/laser-template/label-machine"));
const NewTemplate = lazy(() => import("../pages/labelling/[slug]/laser-template/new-template"));
const ExportToLightBurn = lazy(() => import("../pages/labelling/[slug]/export-to-lightburn"));
const LabelView = lazy(() => import("../pages/labelling/[slug]/label-view"));
// Beta labelling — self-contained copy under pages/beta-labelling. Shares the
// station/label data but none of the production labelling render/export code.
const BetaLabelling = lazy(() => import("../pages/beta-labelling/index"));
const BetaSingleStation = lazy(() => import("../pages/beta-labelling/[slug]"));
const Catlog = lazy(() => import("../pages/catalog/index"));
const CatlogGame = lazy(() => import("../pages/catalog/[gameCode]/index"));
const CatlogSet = lazy(() => import("../pages/catalog/[gameCode]/[setId]"));
const CatlogExport = lazy(() => import("../pages/catalog/[gameCode]/export"));
const CatlogFinishManager = lazy(() => import("../pages/catalog/[gameCode]/finish-manager"));
const Gradings = lazy(() => import("../pages/gradings/index"));
const UpdateGrading = lazy(() => import("../pages/gradings/[gradingId]"));
const Orders = lazy(() => import("../pages/orders/index"));
const OverdueOrders = lazy(() => import("../pages/overdue-orders"));
const User = lazy(() => import("../pages/account/users/index"));
const CreateUserView = lazy(() => import("../pages/account/users/new"));
const UpdateUserView = lazy(() => import("../pages/account/users/[userId]"));
const InstalledStores = lazy(() => import("../pages/account/installed-stores/index"));
const StorefrontContact = lazy(() => import("../pages/account/storefront-contact/index"));
const UpdateCardNotes = lazy(() => import("../pages/card/notes/[gradingId]"));
const UpdateCardView = lazy(() => import("../pages/card/[gradingId]"));
const ShopifyVariants = lazy(() => import("../pages/resources/shopify-variants"));
const WebhookSettings = lazy(() => import("../pages/resources/webhook-settings"));
const CustomerLookup = lazy(() => import("../pages/resources/customer-lookup"));
const Label = lazy(() => import("../pages/label/index"));
const PurchaseDetail = lazy(() => import("../pages/purchaseDetail/purchase"));
const LoyaltyDetail = lazy(() => import("../pages/loyaltyDetail/loyalty"));
const FinishView = lazy(() => import("../pages/finish/[gameCode]/[setId]/index"));
const Documents = lazy(() => import("../pages/documentDetail/document"));
const MarketingPage = lazy(() => import("../pages/marketing/index"));
const GradeCheck = lazy(() => import("../pages/gradeCheck"));
const MultiLanguage = lazy(() => import("../pages/multilanguage"));
const SetNameTranslations = lazy(() => import("../pages/set-name-translations"));
const DashboardOptions = lazy(() => import("../pages/DashboardOptions"));
const CustomersView = lazy(() => import("../pages/customers"));
const MysteryOrders = lazy(() => import("../pages/mystery-orders/index"));
const AddMysteryCard = lazy(() => import("../pages/mystery-orders/add"));
const RegionSettingsPage = lazy(() => import("../pages/settings/region-settings"));
const WorkflowEditorPage = lazy(() => import("../pages/settings/workflow-editor"));
const AutomatedView = lazy(() => import("../pages/automated/index"));
const AutomatedGameView = lazy(() => import("../pages/automated/[gameCode]/index"));
const AutomatedSetView = lazy(() => import("../pages/automated/[gameCode]/[setId]"));
const AiReviewView = lazy(() => import("../pages/automated/[gameCode]/ai-review"));
const FetchLogsView = lazy(() => import("../pages/automated/fetch-logs"));
const CatalogDashboard = lazy(() => import("../pages/catalog-dashboard/index"));
const CatalogCheckerView = lazy(() => import("../pages/catalog-checker/index"));
const CatalogCheckerGameView = lazy(() => import("../pages/catalog-checker/[gameCode]/index"));
const CatalogCheckerSetView = lazy(() => import("../pages/catalog-checker/[gameCode]/[setId]"));
const SetDuplicates = lazy(() => import("../pages/catalog-duplicates/sets/index"));
const SetDuplicateReview = lazy(() => import("../pages/catalog-duplicates/sets/review"));
const ProductDuplicates = lazy(() => import("../pages/catalog-duplicates/products/index"));
const ProductDuplicateReview = lazy(() => import("../pages/catalog-duplicates/products/review"));
const ManualEntries = lazy(() => import("../pages/catalog-duplicates/manual-entries/index"));
const ActivityLogView = lazy(() => import("../pages/activity-log/index"));
const OfficeStatistics = lazy(() => import("../pages/office-statistics"));
const AuditHistory = lazy(() => import("../pages/audit-history/index"));
const SupportChat = lazy(() => import("../pages/support/index"));
const NfcSetupPage = lazy(() => import("../pages/support/nfc-setup"));
const Requests = lazy(() => import("../pages/requests/index"));
const RequestThread = lazy(() => import("../pages/requests/[id]"));
const NotificationsPage = lazy(() => import("../pages/notifications/index"));
const EquipmentDashboardPage = lazy(() => import("../pages/equipment/index"));
const GradingStandardsPage = lazy(() => import("../pages/grading-standards/index"));
const CalibrationPage = lazy(() => import("../pages/calibration/index"));
const RoyaltiesPage = lazy(() => import("../pages/royalties/index"));
const StockPage = lazy(() => import("../pages/stock/index"));
const OnboardingPage = lazy(() => import("../pages/training/onboarding"));
const NewGraderPage = lazy(() => import("../pages/training/newGrader"));
const AuthenticationPage = lazy(() => import("../pages/authentication/index"));
const PartnersPage = lazy(() => import("../pages/partners/index"));

function AppRouting() {
  return (
    <Suspense fallback={<Loader />}>
        <Switch>
           <Route exact path="/" component={Dashboard} />
          <Route exact path="/dashboard" component={Dashboard} />
          <Route exact path="/labelling" component={Labelling} />
          <Route path="/labelling/guides" component={Guides} />
          <Route path="/labelling/edit-stations" component={EditStations} />
          <Route path="/labelling/server-settings" component={ServerSettings} />
          <Route path="/labelling/bug-report" component={BugReport} />
          <Route path="/labelling/editor" component={LabelEditor} />
          <Route path="/labelling/generator" component={LabelGenerator} />
          <Route path="/labelling/designs" component={LabelDesigner} />
          <Route path="/labelling/:slug/label-view" component={LabelView} />
          <Route path="/labelling/:slug/export-to-lightburn" component={ExportToLightBurn} />
          <Route path="/labelling/:slug/laser-template/new-template" component={NewTemplate} />
          <Route path="/labelling/:slug/laser-template/label-machine" component={LabelMachine} />
          <Route path="/labelling/:slug/laser-template" component={LaserTemplate} />
          <Route path="/labelling/:slug" component={SingleStation} />
          <Route exact path="/beta-labelling" component={BetaLabelling} />
          <Route path="/beta-labelling/:slug" component={BetaSingleStation} />
          <Route path="/catalog/:gameCode/export" component={CatlogExport} />
          <Route path="/catalog/:gameCode/finish-manager" component={CatlogFinishManager} />
          <Route path="/catalog/:gameCode/:setId" component={CatlogSet} />
          <Route path="/catalog/:gameCode" component={CatlogGame} />
          <Route path="/catalog" component={Catlog} />

          <Route path="/gradings" component={Gradings} />
          <Route path="/orders" component={Orders} />
          <Route path="/overdue-orders" component={OverdueOrders} />
          <Route path="/mystery-orders/add" component={AddMysteryCard} />
          <Route path="/mystery-orders" component={MysteryOrders} />
          <Route path="/settings/region-settings" component={RegionSettingsPage} />
          <Route path="/settings/workflow-editor" component={WorkflowEditorPage} />
          <Route path="/account/installed-stores" component={InstalledStores} />
          <Route path="/account/storefront-contact" component={StorefrontContact} />
          <Route path="/account/users/new" component={CreateUserView} />
          <Route path="/account/users/:userId" component={UpdateUserView} />
          <Route path="/account/users" component={User} />

          <Route path="/resources/shopify-variants" component={ShopifyVariants} />
          <Route path="/resources/webhook-settings" component={WebhookSettings} />
          <Route path="/resources/customer-lookup" component={CustomerLookup} />
          <Route path="/label" component={Label} />
          <Route path="/purchaseDetail/purchase" component={PurchaseDetail} />
          <Route path="/loyaltyDetail/loyalty" component={LoyaltyDetail} />
          <Route path="/finish/:gameCode/:setId" component={FinishView} />
          <Route path="/documentDetail/document" component={Documents} />
          <Route path="/grade-check" component={GradeCheck} />
          <Route path="/gradeCheck" component={GradeCheck} />
          <Route path="/multi-language" component={MultiLanguage} />
          <Route path="/set-name-translations" component={SetNameTranslations} />
          <Route path="/defectDetail/:route" component={DashboardOptions} />
          <Route path="/errorDetail/:route" component={DashboardOptions} />
          <Route path="/fakeDetail/:route" component={DashboardOptions} />
          <Route path="/authenticityDetail/:route" component={DashboardOptions} />
          <Route path="/signatureDetail/:route" component={DashboardOptions} />
          {/* Review routes sit above their list route — "/sets/review" would otherwise be swallowed. */}
          <Route path="/catalog-duplicates/sets/review" component={SetDuplicateReview} />
          <Route path="/catalog-duplicates/sets" component={SetDuplicates} />
          <Route path="/catalog-duplicates/products/review" component={ProductDuplicateReview} />
          <Route path="/catalog-duplicates/products" component={ProductDuplicates} />
          <Route path="/catalog-duplicates/manual-entries" component={ManualEntries} />
          <Route path="/automated/fetch-logs" component={FetchLogsView} />
          {/* Above the :setId route — "ai-review" would otherwise match as a set id. */}
          <Route path="/automated/:gameCode/ai-review" component={AiReviewView} />
          <Route path="/automated/:gameCode/:setId" component={AutomatedSetView} />
          <Route path="/automated/:gameCode" component={AutomatedGameView} />
          <Route path="/automated" component={AutomatedView} />
          <Route path="/catalog-dashboard" component={CatalogDashboard} />
          <Route path="/catalog-checker/:gameCode/:setId" component={CatalogCheckerSetView} />
          <Route path="/catalog-checker/:gameCode" component={CatalogCheckerGameView} />
          <Route path="/catalog-checker" component={CatalogCheckerView} />
          <Route path="/customers" component={CustomersView} />
          <Route path="/activity-log" component={ActivityLogView} />
          <Route path="/office-statistics" component={OfficeStatistics} />
          <Route path="/audit-history" component={AuditHistory} />
          <Route path="/support" component={SupportChat} />
          <Route path="/nfc-setup" component={NfcSetupPage} />
          <Route path="/requests/:id" component={RequestThread} />
          <Route path="/requests" component={Requests} />
          <Route path="/notifications" component={NotificationsPage} />
          <Route path="/equipment" component={EquipmentDashboardPage} />
          <Route path="/grading-standards" component={GradingStandardsPage} />
          <Route path="/calibration" component={CalibrationPage} />
          <Route path="/royalties" component={RoyaltiesPage} />
          <Route path="/stock" component={StockPage} />
          <Route path="/onboarding" component={OnboardingPage} />
          <Route path="/new-grader" component={NewGraderPage} />
          <Route path="/authentication" component={AuthenticationPage} />
          <Route path="/partners" component={PartnersPage} />
          <Redirect from="/marketing/marketingAssets" to="/marketing" />
          <Route path="/marketing" component={MarketingPage} />
          {/*
            Browsing the catalog, raising an order and checking what is on its way are one job
            with three moments, so they are one page now. The old paths redirect rather than 404:
            they are in people's bookmarks and in links sent between offices.
          */}
          <Redirect from="/equipment-catalog" to="/equipment" />
          <Redirect from="/purchase-orders" to="/equipment" />
          <Redirect from="/fake-check" to="/authentication?tab=guide" />
          <Redirect from="/verification" to="/authentication?tab=registry" />

        </Switch>
    </Suspense>
  );
}

export default AppRouting;
